import React, { useState, useEffect, useMemo } from 'react';
import API from '../services/api';
import { todayEastern } from '../dateUtils';
import { isLimitedAdmin } from '../services/auth';

/* ============================================================
   Utilities
============================================================ */
function formatSessionDate(rawDate) {
  if (!rawDate) return '';
  const datePart = String(rawDate).split('T')[0];
  const [y, m, d] = datePart.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString();
}

function hashString(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return hash;
}
const pastelColors = ['#FFB3BA', '#FFDFBA', '#FFFFBA', '#BAFFC9', '#BAE1FF'];

function getTherapistRowStyle(therapistName = '', paySplitsMap) {
  if (!therapistName) return { backgroundColor: 'var(--surface-muted)' };
  const payObj = paySplitsMap[therapistName];
  if (payObj && payObj.colorCode) return { backgroundColor: payObj.colorCode };
  const h = Math.abs(hashString(therapistName));
  return { backgroundColor: pastelColors[h % pastelColors.length] };
}

function getPaymentStyle(paymentType) {
  const lower = (paymentType || '').toLowerCase();
  let bg = 'var(--border-color)';
  let fg = 'var(--text-strong)';
  if (lower.includes('cash')) { bg = '#16a34a'; fg = '#fff'; }
  else if (lower.includes('card')) { bg = '#2563eb'; fg = '#fff'; }
  else if (lower.includes('check')) { bg = '#eab308'; fg = '#1f2937'; }
  return {
    backgroundColor: bg,
    color: fg,
    padding: '3px 10px',
    borderRadius: '999px',
    display: 'inline-block',
    fontSize: '0.8rem',
    fontWeight: 600,
    letterSpacing: '0.02em',
  };
}

function getSessionStatusBadge(noShow, cancelation) {
  const base = {
    padding: '3px 8px',
    borderRadius: '4px',
    display: 'inline-block',
    fontSize: '0.8rem',
    fontWeight: 700,
  };
  if (noShow) return { label: 'No Show', style: { ...base, backgroundColor: 'var(--error-color)', color: '#fff' } };
  if (cancelation) return { label: 'Canceled', style: { ...base, backgroundColor: '#f97316', color: '#fff' } };
  return { label: '', style: {} };
}

function getStatusPillStyle(status) {
  const s = (status || '').toUpperCase();
  const base = {
    padding: '2px 10px',
    borderRadius: '999px',
    fontSize: '0.78rem',
    fontWeight: 700,
    display: 'inline-block',
  };
  if (s === 'PAID') return { ...base, backgroundColor: '#dcfce7', color: '#166534' };
  if (s === 'SUBMITTED') return { ...base, backgroundColor: '#dbeafe', color: '#1e40af' };
  if (s === 'DEBT') return { ...base, backgroundColor: '#fee2e2', color: '#991b1b' };
  if (s === 'UNPAID') return { ...base, backgroundColor: '#fef3c7', color: '#854d0e' };
  return { ...base, backgroundColor: 'var(--border-color)', color: 'var(--text-strong)' };
}

/* ============================================================
   Component
============================================================ */
function AdminDailyReportPage() {
  const limited = isLimitedAdmin();
  // ---- New (unpaid) sessions ----
  const [newSessions, setNewSessions] = useState([]);
  const [newSortOption, setNewSortOption] = useState('date');
  const [newEditingRows, setNewEditingRows] = useState({});

  // ---- Past sessions ----
  const [statusFilter, setStatusFilter] = useState('SUBMITTED');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [pastSessions, setPastSessions] = useState([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingPast, setLoadingPast] = useState(false);

  const [markPaidMap, setMarkPaidMap] = useState({});
  const [sortColumn, setSortColumn] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  const [editingRows, setEditingRows] = useState({});
  const [payPopupData, setPayPopupData] = useState(null);
  const [therapistPaySplits, setTherapistPaySplits] = useState({});
  const [therapistList, setTherapistList] = useState([]); // for download modal

  // ---- Download modal ----
  const [showDownloadModal, setShowDownloadModal] = useState(false);

  /* ============================================================
     Fetch: New (unpaid) sessions
  ============================================================ */
  const fetchNewSessions = async () => {
    try {
      const res = await API.get('/admin/daily-report/unpaid');
      setNewSessions(sortNewSessions(res.data, newSortOption));
    } catch (err) {
      console.error('Error fetching new sessions:', err);
      alert('Error fetching new sessions');
    }
  };

  const sortNewSessions = (sessions, sortBy) => {
    const s = [...sessions];
    switch (sortBy) {
      case 'date':
        s.sort((a, b) => new Date(a.session_date) - new Date(b.session_date));
        break;
      case 'therapist':
        s.sort((a, b) => {
          const at = (a.therapist_last_name || '') + (a.therapist_first_name || '');
          const bt = (b.therapist_last_name || '') + (b.therapist_first_name || '');
          return at.localeCompare(bt);
        });
        break;
      case 'client':
        s.sort((a, b) => (a.client_name || '').localeCompare(b.client_name || ''));
        break;
      default:
        break;
    }
    return s;
  };

  const handleNewSortChange = (e) => {
    const val = e.target.value;
    setNewSortOption(val);
    setNewSessions((prev) => sortNewSessions(prev, val));
  };

  const handleStatusChange = (sessionId, newStatus) => {
    setNewSessions((prev) =>
      prev.map((s) => (s.id === sessionId ? { ...s, newStatus } : s))
    );
  };

  // FIX: removed the redundant optimistic filter before the refetch
  const handleSubmitChanges = async () => {
    try {
      const updates = newSessions
        .filter((s) => ['PAID', 'DEBT', 'SUBMITTED'].includes(s.newStatus))
        .map((s) => {
          const update = { sessionId: s.id, status: s.newStatus };
          if (s.newStatus === 'PAID' && s.collected_amount && parseFloat(s.collected_amount) > 0) {
            update.preserveCollectedAmount = true;
            update.collectedAmount = s.collected_amount;
          }
          return update;
        });

      if (updates.length === 0) {
        alert('No changes to submit.');
        return;
      }

      await API.put('/admin/daily-report', { updates });
      await fetchNewSessions();
      handleSearchPastSessions(true);
      alert('Daily report updated successfully!');
    } catch (err) {
      console.error('Error updating daily report:', err);
      alert('Error updating daily report');
    }
  };

  /* ============================================================
     Editing: New sessions
  ============================================================ */
  const startEditingNewSession = (row) => {
    setNewEditingRows((prev) => ({
      ...prev,
      [row.id]: {
        ...row,
        session_date: String(row.session_date).split('T')[0],
      },
    }));
  };
  const cancelEditingNewSession = (id) => {
    setNewEditingRows((prev) => {
      const { [id]: _, ...rest } = prev;
      return rest;
    });
  };
  const handleEditNewChange = (id, field, value) => {
    setNewEditingRows((prev) => ({
      ...prev,
      [id]: { ...prev[id], [field]: value },
    }));
  };
  const saveNewEdits = async (id) => {
    const rowToSave = newEditingRows[id];
    if (!rowToSave) return;
    try {
      await API.put('/admin/daily-report/update', {
        id: rowToSave.id,
        status: rowToSave.status,
        billed_amount: rowToSave.billed_amount,
        collected_amount: rowToSave.collected_amount,
        notes: rowToSave.notes,
        service_type: rowToSave.service_type,
        session_date: rowToSave.session_date,
        no_show: rowToSave.no_show || false,
        cancelation: rowToSave.cancelation || false,
      });
      alert('Entry updated!');
      setNewSessions((prev) =>
        prev.map((ns) => (ns.id === id ? { ...ns, ...rowToSave } : ns))
      );
      cancelEditingNewSession(id);
    } catch (err) {
      console.error('Error saving new session edits:', err);
      alert('Error saving new session edits');
    }
  };
  const deleteNewSession = async (id) => {
    if (!window.confirm('Delete this session?')) return;
    try {
      await API.delete(`/admin/daily-report/${id}`);
      alert('Session deleted');
      setNewSessions((prev) => prev.filter((ns) => ns.id !== id));
      cancelEditingNewSession(id);
    } catch (err) {
      console.error('Error deleting session:', err);
      alert('Error deleting session');
    }
  };

  /* ============================================================
     Fetch: Past sessions

     When viewing SUBMITTED, we fetch everything (no limit) per product
     requirement. For all other statuses we keep the 20-per-page loader.
  ============================================================ */
  const handleSearchPastSessions = async (resetPage = true) => {
    try {
      setLoadingPast(true);
      const isSubmittedView = statusFilter === 'SUBMITTED';

      let url = '/admin/daily-report/history?';
      const parts = [];
      if (isSubmittedView) {
        parts.push('status=SUBMITTED');
      } else {
        const pageToUse = resetPage ? 0 : page;
        if (resetPage) setPage(0);
        parts.push(`limit=21&offset=${pageToUse * 20}`);
        parts.push(`status=${statusFilter}`);
      }
      if (startDate) parts.push(`start=${startDate}`);
      if (endDate) parts.push(`end=${endDate}`);
      if (searchTerm) parts.push(`searchTerm=${encodeURIComponent(searchTerm)}`);
      url += parts.join('&');

      const res = await API.get(url);
      const data = res.data || [];

      if (isSubmittedView) {
        setPastSessions(data);
        setHasMore(false);
        // Auto-select every returned submitted row for Mark-Paid workflow
        if (resetPage) {
          const next = {};
          for (const ps of data) {
            if ((ps.status || '').toUpperCase() === 'SUBMITTED') {
              next[ps.id] = true;
            }
          }
          setMarkPaidMap(next);
        }
      } else {
        const weHaveMore = data.length > 20;
        const first20 = data.slice(0, 20);
        if (resetPage) setPastSessions(first20);
        else setPastSessions((prev) => [...prev, ...first20]);
        setHasMore(weHaveMore);
      }
    } catch (error) {
      console.error('Error fetching historical sessions:', error);
      alert('Error fetching historical sessions');
    } finally {
      setLoadingPast(false);
    }
  };

  const handleLoadOlder = () => setPage((p) => p + 1);

  /* ============================================================
     Sorting: Past sessions
  ============================================================ */
  const getSortValue = (row, column) => {
    switch (column) {
      case 'therapist':
        return (row.therapist_last_name || '') + (row.therapist_first_name || '');
      case 'session_date':
        return row.session_date ? new Date(row.session_date + 'T12:00:00').getTime() : 0;
      case 'client': return row.client_name || '';
      case 'billed': return parseFloat(row.billed_amount) || 0;
      case 'collected': return parseFloat(row.collected_amount) || 0;
      case 'payment': return row.payment_type || '';
      case 'notes': return row.notes || '';
      case 'status': return row.status || '';
      case 'session_status':
        if (row.no_show) return 'No Show';
        if (row.cancelation) return 'Canceled';
        return '';
      default: return '';
    }
  };
  const handleColumnSort = (column) => {
    if (sortColumn === column) {
      setSortDirection((dir) => (dir === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };
  const sortedPastSessions = useMemo(() => {
    if (!sortColumn) return pastSessions;
    const sorted = [...pastSessions].sort((a, b) => {
      const av = getSortValue(a, sortColumn);
      const bv = getSortValue(b, sortColumn);
      if (av < bv) return -1;
      if (av > bv) return 1;
      return 0;
    });
    return sortDirection === 'asc' ? sorted : sorted.reverse();
  }, [pastSessions, sortColumn, sortDirection]);

  /* ============================================================
     Editing: Past sessions
  ============================================================ */
  const startEditing = (ps) => {
    setEditingRows((prev) => ({
      ...prev,
      [ps.id]: {
        ...ps,
        session_date: String(ps.session_date).split('T')[0],
      },
    }));
  };
  const cancelEditing = (id) => {
    setEditingRows((prev) => {
      const { [id]: _, ...rest } = prev;
      return rest;
    });
  };
  const handleEditChange = (id, field, value) => {
    setEditingRows((prev) => ({
      ...prev,
      [id]: { ...prev[id], [field]: value },
    }));
  };
  const saveEdits = async (id) => {
    const rowToSave = editingRows[id];
    if (!rowToSave) return;
    try {
      await API.put('/admin/daily-report/update', {
        id: rowToSave.id,
        status: rowToSave.status,
        billed_amount: rowToSave.billed_amount,
        collected_amount: rowToSave.collected_amount,
        notes: rowToSave.notes,
        service_type: rowToSave.service_type,
        session_date: rowToSave.session_date,
        no_show: rowToSave.no_show || false,
        cancelation: rowToSave.cancelation || false,
      });
      alert('Entry updated!');
      setPastSessions((prev) =>
        prev.map((ps) => (ps.id === id ? { ...ps, ...rowToSave } : ps))
      );
      cancelEditing(id);
    } catch (err) {
      console.error('Error saving edits:', err);
      alert('Error saving edits.');
    }
  };
  const deletePastSession = async (id) => {
    if (!window.confirm('Delete this session?')) return;
    try {
      await API.delete(`/admin/daily-report/${id}`);
      alert('Session deleted');
      setPastSessions((prev) => prev.filter((ps) => ps.id !== id));
      cancelEditing(id);
    } catch (err) {
      console.error('Error deleting session:', err);
      alert('Could not delete session.');
    }
  };

  /* ============================================================
     Mark-as-paid
  ============================================================ */
  const handleToggleMarkPaid = (id) => {
    setMarkPaidMap((prev) => ({ ...prev, [id]: !prev[id] }));
  };
  const selectAllSubmittedVisible = () => {
    const next = {};
    for (const ps of sortedPastSessions) {
      if ((ps.status || '').toUpperCase() === 'SUBMITTED') {
        next[ps.id] = true;
      }
    }
    setMarkPaidMap(next);
  };
  const clearSelection = () => setMarkPaidMap({});

  const handleMarkSelectedAsPaid = async () => {
    const selectedIds = Object.keys(markPaidMap).filter((sid) => markPaidMap[sid]);
    if (selectedIds.length === 0) {
      alert('No sessions selected to mark as paid.');
      return;
    }

    // Backend marks PAID, computes authoritative splits + pay-period take-home,
    // and records the batch to the Submission Log. It returns the breakdown for
    // the confirmation popup.
    let res;
    try {
      res = await API.post('/admin/daily-report/mark-paid', {
        sessionIds: selectedIds.map((sid) => +sid),
      });
    } catch (err) {
      console.error('Error marking sessions as paid:', err);
      alert('Error marking sessions as paid');
      return;
    }

    setPayPopupData(res.data.breakdown || []);
    handleSearchPastSessions(true);
    setMarkPaidMap({});
  };

  /* ============================================================
     Therapist pay splits — single consolidated fetch (FIX: was N+1)
  ============================================================ */
  const fetchAllTherapistsPaySplit = async () => {
    try {
      const res = await API.get('/admin/all-therapists-with-goals');
      const all = res.data || [];
      const payMap = {};
      for (const t of all) {
        const nameKey = `${t.first_name} ${t.last_name}`.trim();
        payMap[nameKey] = {
          therapistId: t.id,
          paySplit: t.pay_split_percent ?? '50',
          colorCode: t.color_code || '#FFFFFF',
        };
      }
      setTherapistPaySplits(payMap);
      setTherapistList(all);
    } catch (err) {
      console.error('Error loading therapist pay splits/colors:', err);
    }
  };

  /* ============================================================
     Effects
  ============================================================ */
  useEffect(() => {
    fetchNewSessions();
    handleSearchPastSessions(true);
    fetchAllTherapistsPaySplit();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (page > 0) {
      handleSearchPastSessions(false);
    }
    // eslint-disable-next-line
  }, [page]);

  /* ============================================================
     Derived: submitted-view summary strip
  ============================================================ */
  const submittedSummary = useMemo(() => {
    if (statusFilter !== 'SUBMITTED') return null;
    let totalBilled = 0;
    let totalCollected = 0;
    for (const ps of pastSessions) {
      totalBilled += parseFloat(ps.billed_amount) || 0;
      totalCollected += parseFloat(ps.collected_amount) || 0;
    }
    return {
      count: pastSessions.length,
      totalBilled,
      totalCollected,
    };
  }, [pastSessions, statusFilter]);

  /* ============================================================
     Render
  ============================================================ */
  return (
    <div className="admin-daily-report-container" style={containerStyle}>
      <header style={pageHeaderStyle}>
        <div>
          <h2 style={{ margin: 0 }}>Daily Report</h2>
          <p style={{ margin: '4px 0 0', color: 'var(--text-muted)', fontSize: '0.9rem' }}>
            Review, adjust, and submit session payments
          </p>
        </div>
      </header>

      {/* ===================== PART 1: New Sessions ===================== */}
      <section style={cardStyle}>
        <div style={sectionHeaderStyle}>
          <h3 style={{ margin: 0 }}>New Sessions</h3>
          <div className="daily-report-controls" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <label style={{ fontSize: '0.9rem', color: '#4b5563' }}>Sort by</label>
            <select value={newSortOption} onChange={handleNewSortChange} style={selectStyle}>
              <option value="date">Date</option>
              <option value="therapist">Therapist</option>
              <option value="client">Client</option>
            </select>
          </div>
        </div>

        {newSessions.length === 0 ? (
          <p style={{ color: 'var(--text-muted)' }}>No new sessions found.</p>
        ) : (
          <>
            <div style={tableWrapStyle}>
              <table className="daily-report-table" style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>Date</th>
                    <th style={thStyle}>Therapist</th>
                    <th style={thStyle}>Client</th>
                    <th style={thStyle}>Billed</th>
                    <th style={thStyle}>Collected</th>
                    <th style={thStyle}>Payment</th>
                    <th style={thStyle}>Notes</th>
                    <th style={thStyle}>Session Status</th>
                    <th style={thStyle}>Mark as</th>
                    <th style={thStyle}>Edit</th>
                  </tr>
                </thead>
                <tbody>
                  {newSessions.map((s) => {
                    const isEditing = !!newEditingRows[s.id];
                    const editRow = newEditingRows[s.id] || {};
                    const therapistName = `${s.therapist_first_name || ''} ${s.therapist_last_name || ''}`.trim();
                    const sessionStatus = getSessionStatusBadge(s.no_show, s.cancelation);

                    return (
                      <tr key={s.id} style={{ ...getTherapistRowStyle(therapistName, therapistPaySplits), ...trStyle }}>
                        <td style={tdStyle}>
                          {isEditing ? (
                            <input
                              type="date"
                              value={editRow.session_date}
                              onChange={(e) => handleEditNewChange(s.id, 'session_date', e.target.value)}
                              style={inputStyle}
                            />
                          ) : (formatSessionDate(s.session_date))}
                        </td>
                        <td style={tdStyle}>{s.therapist_first_name} {s.therapist_last_name}</td>
                        <td style={tdStyle}>{s.client_name}</td>
                        <td style={tdStyle}>
                          {isEditing ? (
                            <input
                              type="number"
                              style={{ ...inputStyle, width: '80px' }}
                              value={editRow.billed_amount}
                              onChange={(e) => handleEditNewChange(s.id, 'billed_amount', e.target.value)}
                            />
                          ) : (`$${s.billed_amount}`)}
                        </td>
                        <td style={tdStyle}>
                          {isEditing ? (
                            <input
                              type="number"
                              style={{ ...inputStyle, width: '80px' }}
                              value={editRow.collected_amount || 0}
                              onChange={(e) => handleEditNewChange(s.id, 'collected_amount', e.target.value)}
                            />
                          ) : (`$${s.collected_amount || 0}`)}
                        </td>
                        <td style={tdStyle}>
                          <span style={getPaymentStyle(s.payment_type)}>{s.payment_type || ''}</span>
                        </td>
                        <td style={tdStyle}>
                          {isEditing ? (
                            <input
                              type="text"
                              style={{ ...inputStyle, width: '100px' }}
                              value={editRow.notes || ''}
                              onChange={(e) => handleEditNewChange(s.id, 'notes', e.target.value)}
                            />
                          ) : (s.notes || '')}
                        </td>
                        <td style={tdStyle}>
                          {isEditing ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                              <label><input type="checkbox" checked={!!editRow.no_show} onChange={(e) => handleEditNewChange(s.id, 'no_show', e.target.checked)} /> No Show</label>
                              <label><input type="checkbox" checked={!!editRow.cancelation} onChange={(e) => handleEditNewChange(s.id, 'cancelation', e.target.checked)} /> Canceled</label>
                            </div>
                          ) : (
                            sessionStatus.label && <span style={sessionStatus.style}>{sessionStatus.label}</span>
                          )}
                        </td>
                        <td style={tdStyle}>
                          <div className="mark-as-container" style={{ display: 'flex', flexDirection: 'column', gap: '2px', fontSize: '0.85rem' }}>
                            <label><input type="radio" name={`status-${s.id}`} onChange={() => handleStatusChange(s.id, null)} checked={!s.newStatus} /> No Change</label>
                            <label><input type="radio" name={`status-${s.id}`} onChange={() => handleStatusChange(s.id, 'PAID')} checked={s.newStatus === 'PAID'} /> Paid</label>
                            <label><input type="radio" name={`status-${s.id}`} onChange={() => handleStatusChange(s.id, 'DEBT')} checked={s.newStatus === 'DEBT'} /> Debt</label>
                            <label><input type="radio" name={`status-${s.id}`} onChange={() => handleStatusChange(s.id, 'SUBMITTED')} checked={s.newStatus === 'SUBMITTED'} /> Submitted</label>
                          </div>
                        </td>
                        <td style={tdStyle}>
                          {isEditing ? (
                            <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
                              <button style={btnSmallStyle} onClick={() => saveNewEdits(s.id)}>Save</button>
                              <button style={{ ...btnSmallStyle, ...btnDangerStyle }} onClick={() => deleteNewSession(s.id)}>Delete</button>
                              <button style={btnSmallStyle} onClick={() => cancelEditingNewSession(s.id)}>Cancel</button>
                            </div>
                          ) : (
                            !limited ? <button style={btnSmallStyle} onClick={() => startEditingNewSession(s)}>Edit</button> : null
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <button
              className="daily-report-submit-btn"
              onClick={handleSubmitChanges}
              style={{ ...btnPrimaryStyle, marginTop: '1rem' }}
            >
              Submit Changes
            </button>
          </>
        )}
      </section>

      {/* ===================== PART 2: Past Sessions ===================== */}
      <section style={cardStyle}>
        <div style={sectionHeaderStyle}>
          <h3 style={{ margin: 0 }}>Past Sessions</h3>
          <button onClick={() => setShowDownloadModal(true)} style={btnPrimaryStyle}>
            ⬇ Download Ledger
          </button>
        </div>

        {/* Filters */}
        <div style={filtersRowStyle}>
          <div style={filterGroupStyle}>
            <label style={filterLabelStyle}>Start Date</label>
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} style={inputStyle} />
          </div>
          <div style={filterGroupStyle}>
            <label style={filterLabelStyle}>End Date</label>
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} style={inputStyle} />
          </div>
          <div style={filterGroupStyle}>
            <label style={filterLabelStyle}>Search</label>
            <input
              type="text"
              placeholder="Client or Therapist"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={inputStyle}
            />
          </div>
          <div style={{ ...filterGroupStyle, alignSelf: 'flex-end' }}>
            <button onClick={() => handleSearchPastSessions(true)} style={btnPrimaryStyle}>
              Search
            </button>
          </div>
        </div>

        {/* Status filter pills */}
        <div style={{ display: 'flex', gap: '0.5rem', margin: '0.75rem 0', flexWrap: 'wrap' }}>
          {['ALL', 'UNPAID', 'PAID', 'DEBT', 'SUBMITTED'].map((st) => (
            <button
              key={st}
              onClick={() => {
                setStatusFilter(st);
                setTimeout(() => handleSearchPastSessions(true), 0);
              }}
              style={{
                ...pillStyle,
                ...(statusFilter === st ? pillActiveStyle : {}),
              }}
            >
              {st.charAt(0) + st.slice(1).toLowerCase()}
            </button>
          ))}
        </div>

        {/* Submitted-view summary strip */}
        {submittedSummary && (
          <div style={summaryStripStyle}>
            <div>
              <div style={summaryLabelStyle}>Submitted Entries</div>
              <div style={summaryValueStyle}>{submittedSummary.count}</div>
            </div>
            <div>
              <div style={summaryLabelStyle}>Total Billed</div>
              <div style={summaryValueStyle}>${submittedSummary.totalBilled.toFixed(2)}</div>
            </div>
            <div>
              <div style={summaryLabelStyle}>Total Collected</div>
              <div style={summaryValueStyle}>${submittedSummary.totalCollected.toFixed(2)}</div>
            </div>
          </div>
        )}

        {/* Bulk selection */}
        <div style={{ margin: '0.5rem 0', display: 'flex', gap: '0.5rem' }}>
          <button onClick={selectAllSubmittedVisible} style={btnSmallStyle}>
            Select All Submitted (visible)
          </button>
          <button onClick={clearSelection} style={btnSmallStyle}>
            Clear Selection
          </button>
        </div>

        {loadingPast ? (
          <p style={{ color: 'var(--text-muted)' }}>Loading…</p>
        ) : sortedPastSessions.length === 0 ? (
          <p style={{ color: 'var(--text-muted)' }}>No past sessions found.</p>
        ) : (
          <>
            <div style={tableWrapStyle}>
              <table className="daily-report-table" style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thSortStyle} onClick={() => handleColumnSort('session_date')}>Date {sortColumn === 'session_date' ? (sortDirection === 'asc' ? '▲' : '▼') : ''}</th>
                    <th style={thSortStyle} onClick={() => handleColumnSort('therapist')}>Therapist {sortColumn === 'therapist' ? (sortDirection === 'asc' ? '▲' : '▼') : ''}</th>
                    <th style={thSortStyle} onClick={() => handleColumnSort('client')}>Client {sortColumn === 'client' ? (sortDirection === 'asc' ? '▲' : '▼') : ''}</th>
                    <th style={thSortStyle} onClick={() => handleColumnSort('billed')}>Billed {sortColumn === 'billed' ? (sortDirection === 'asc' ? '▲' : '▼') : ''}</th>
                    <th style={thSortStyle} onClick={() => handleColumnSort('collected')}>Collected {sortColumn === 'collected' ? (sortDirection === 'asc' ? '▲' : '▼') : ''}</th>
                    <th style={thSortStyle} onClick={() => handleColumnSort('payment')}>Payment</th>
                    <th style={thSortStyle} onClick={() => handleColumnSort('notes')}>Notes</th>
                    <th style={thSortStyle} onClick={() => handleColumnSort('session_status')}>Session Status</th>
                    <th style={thSortStyle} onClick={() => handleColumnSort('status')}>Status</th>
                    <th style={thStyle}>Edit</th>
                    <th style={thStyle}>Mark Paid</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedPastSessions.map((ps) => {
                    const isEditing = !!editingRows[ps.id];
                    const editRow = editingRows[ps.id];
                    const therapistName = `${ps.therapist_first_name} ${ps.therapist_last_name}`.trim();
                    const sessionStatus = getSessionStatusBadge(ps.no_show, ps.cancelation);

                    return (
                      <tr key={ps.id} style={{ ...getTherapistRowStyle(therapistName, therapistPaySplits), ...trStyle }}>
                        <td style={tdStyle}>
                          {isEditing ? (
                            <input type="date" value={editRow.session_date} onChange={(e) => handleEditChange(ps.id, 'session_date', e.target.value)} style={inputStyle} />
                          ) : (formatSessionDate(ps.session_date))}
                        </td>
                        <td style={tdStyle}>{ps.therapist_first_name} {ps.therapist_last_name}</td>
                        <td style={tdStyle}>{ps.client_name}</td>
                        <td style={tdStyle}>
                          {isEditing ? (
                            <input type="number" value={editRow.billed_amount} onChange={(e) => handleEditChange(ps.id, 'billed_amount', e.target.value)} style={{ ...inputStyle, width: '80px' }} />
                          ) : (`$${ps.billed_amount}`)}
                        </td>
                        <td style={tdStyle}>
                          {isEditing ? (
                            <input type="number" value={editRow.collected_amount} onChange={(e) => handleEditChange(ps.id, 'collected_amount', e.target.value)} style={{ ...inputStyle, width: '80px' }} />
                          ) : (`$${ps.collected_amount}`)}
                        </td>
                        <td style={tdStyle}>
                          <span style={getPaymentStyle(ps.payment_type)}>{ps.payment_type || ''}</span>
                        </td>
                        <td style={tdStyle}>
                          {isEditing ? (
                            <input type="text" value={editRow.notes || ''} onChange={(e) => handleEditChange(ps.id, 'notes', e.target.value)} style={{ ...inputStyle, width: '100px' }} />
                          ) : (ps.notes || '')}
                        </td>
                        <td style={tdStyle}>
                          {isEditing ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                              <label><input type="checkbox" checked={!!editRow.no_show} onChange={(e) => handleEditChange(ps.id, 'no_show', e.target.checked)} /> No Show</label>
                              <label><input type="checkbox" checked={!!editRow.cancelation} onChange={(e) => handleEditChange(ps.id, 'cancelation', e.target.checked)} /> Canceled</label>
                            </div>
                          ) : (
                            sessionStatus.label && <span style={sessionStatus.style}>{sessionStatus.label}</span>
                          )}
                        </td>
                        <td style={tdStyle}>
                          {isEditing ? (
                            <select value={editRow.status || ''} onChange={(e) => handleEditChange(ps.id, 'status', e.target.value)} style={selectStyle}>
                              <option value="UNPAID">Unpaid</option>
                              <option value="PAID">Paid</option>
                              <option value="DEBT">Debt</option>
                              <option value="SUBMITTED">Submitted</option>
                            </select>
                          ) : (
                            <span style={getStatusPillStyle(ps.status)}>{ps.status}</span>
                          )}
                        </td>
                        <td style={tdStyle}>
                          {isEditing ? (
                            <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
                              <button style={btnSmallStyle} onClick={() => saveEdits(ps.id)}>Save</button>
                              <button style={{ ...btnSmallStyle, ...btnDangerStyle }} onClick={() => deletePastSession(ps.id)}>Delete</button>
                              <button style={btnSmallStyle} onClick={() => cancelEditing(ps.id)}>Cancel</button>
                            </div>
                          ) : (
                            !limited ? <button style={btnSmallStyle} onClick={() => startEditing(ps)}>Edit</button> : null
                          )}
                        </td>
                        <td style={tdStyle}>
                          <input
                            type="checkbox"
                            checked={!!markPaidMap[ps.id]}
                            onChange={() => handleToggleMarkPaid(ps.id)}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {hasMore && (
              <button onClick={handleLoadOlder} style={{ ...btnStyle, marginTop: '1rem' }}>
                Load Older Entries
              </button>
            )}

            <div style={{ marginTop: '1rem' }}>
              {!limited && (
              <button onClick={handleMarkSelectedAsPaid} style={btnPrimaryStyle}>
                Mark Selected as Paid
              </button>
              )}
            </div>
          </>
        )}
      </section>

      {/* ===================== Payment Summary modal ===================== */}
      {payPopupData && (
        <Modal onClose={() => setPayPopupData(null)} title="Payment Summary">
          <table style={modalTableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Therapist</th>
                <th style={thStyle}>Total Collected</th>
                <th style={thStyle}>Pay Split %</th>
                <th style={thStyle}>Therapist Pay</th>
                <th style={thStyle}>Practice</th>
                <th style={thStyle}>Pay-Period Take-Home</th>
              </tr>
            </thead>
            <tbody>
              {payPopupData.map((row, idx) => (
                <tr key={idx}>
                  <td style={tdStyle}>{row.name}</td>
                  <td style={tdStyle}>${Number(row.totalCollected).toFixed(2)}</td>
                  <td style={tdStyle}>{Number(row.paySplit).toFixed(2)}%</td>
                  <td style={tdStyle}>${Number(row.therapistPay).toFixed(2)}</td>
                  <td style={tdStyle}>${Number(row.practicePay).toFixed(2)}</td>
                  <td style={{ ...tdStyle, fontWeight: 700 }}>
                    {row.periodTakeHome != null ? `$${Number(row.periodTakeHome).toFixed(2)}` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p style={{ margin: '0.75rem 0 0', color: 'var(--text-muted)', fontSize: '0.82rem' }}>
            Take-home this pay period (since last payroll run), after split.
          </p>
          <div style={{ textAlign: 'right', marginTop: '1rem' }}>
            <button onClick={() => setPayPopupData(null)} style={btnPrimaryStyle}>Close</button>
          </div>
        </Modal>
      )}

      {/* ===================== Download Ledger modal ===================== */}
      {showDownloadModal && (
        <DownloadLedgerModal
          therapistList={therapistList}
          onClose={() => setShowDownloadModal(false)}
        />
      )}
    </div>
  );
}

/* ============================================================
   Modal primitives
============================================================ */
function Modal({ title, children, onClose }) {
  return (
    <div style={modalBackdropStyle} onClick={onClose}>
      <div style={modalCardStyle} onClick={(e) => e.stopPropagation()}>
        <div style={modalHeaderStyle}>
          <h3 style={{ margin: 0 }}>{title}</h3>
          <button onClick={onClose} style={modalCloseBtnStyle} aria-label="Close">×</button>
        </div>
        <div style={{ padding: '1rem' }}>{children}</div>
      </div>
    </div>
  );
}

/* ============================================================
   Download Ledger Modal

   Defaults (per product requirement): ignore the page's current filters,
   start fresh with status=SUBMITTED, no date range, all payment types,
   all therapists.
============================================================ */
function DownloadLedgerModal({ therapistList, onClose }) {
  const [status, setStatus] = useState('SUBMITTED');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [paymentType, setPaymentType] = useState('ALL');
  const [therapistIds, setTherapistIds] = useState([]); // [] = all
  const [includeSummary, setIncludeSummary] = useState(true);
  const [downloading, setDownloading] = useState(false);

  const toggleTherapist = (id) => {
    setTherapistIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const parts = [`status=${encodeURIComponent(status)}`];
      if (start) parts.push(`start=${start}`);
      if (end) parts.push(`end=${end}`);
      if (paymentType && paymentType !== 'ALL') parts.push(`paymentType=${encodeURIComponent(paymentType)}`);
      if (therapistIds.length > 0) parts.push(`therapistIds=${therapistIds.join(',')}`);
      if (includeSummary) parts.push('includeSummary=true');
      const url = `/admin/export-ledger?${parts.join('&')}`;

      // Use the API instance so auth headers are applied, with blob response
      const res = await API.get(url, { responseType: 'blob' });
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const stamp = todayEastern();
      const filename = `ledger_${status.toLowerCase()}_${stamp}.xlsx`;

      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);

      onClose();
    } catch (err) {
      console.error('Error downloading ledger:', err);
      alert('Error downloading ledger. Check the console for details.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Modal title="Download Ledger" onClose={onClose}>
      <p style={{ margin: '0 0 1rem', color: 'var(--text-muted)', fontSize: '0.9rem' }}>
        Default pulls every entry currently marked as submitted for payment.
        Adjust the filters below to narrow the export.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
        <div style={filterGroupStyle}>
          <label style={filterLabelStyle}>Status</label>
          <select value={status} onChange={(e) => setStatus(e.target.value)} style={selectStyle}>
            <option value="SUBMITTED">Submitted (default)</option>
            <option value="ALL">All</option>
            <option value="UNPAID">Unpaid</option>
            <option value="PAID">Paid</option>
            <option value="DEBT">Debt</option>
          </select>
        </div>

        <div style={filterGroupStyle}>
          <label style={filterLabelStyle}>Payment Type</label>
          <select value={paymentType} onChange={(e) => setPaymentType(e.target.value)} style={selectStyle}>
            <option value="ALL">All</option>
            <option value="cash">Cash</option>
            <option value="card">Card</option>
            <option value="check">Check</option>
          </select>
        </div>

        <div style={filterGroupStyle}>
          <label style={filterLabelStyle}>Start Date</label>
          <input type="date" value={start} onChange={(e) => setStart(e.target.value)} style={inputStyle} />
        </div>

        <div style={filterGroupStyle}>
          <label style={filterLabelStyle}>End Date</label>
          <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} style={inputStyle} />
        </div>
      </div>

      <div style={{ marginTop: '1rem' }}>
        <label style={filterLabelStyle}>Therapists</label>
        <div style={modalScrollBoxStyle}>
          {therapistList.length === 0 ? (
            <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>(no therapists available)</div>
          ) : (
            <>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>
                {therapistIds.length === 0
                  ? 'All therapists included (default)'
                  : `${therapistIds.length} therapist(s) selected`}
              </div>
              {therapistList.map((t) => (
                <label key={t.id} style={{ display: 'block', padding: '2px 0', fontSize: '0.9rem' }}>
                  <input
                    type="checkbox"
                    checked={therapistIds.includes(t.id)}
                    onChange={() => toggleTherapist(t.id)}
                  />{' '}
                  {t.first_name} {t.last_name}
                </label>
              ))}
              {therapistIds.length > 0 && (
                <button
                  onClick={() => setTherapistIds([])}
                  style={{ ...btnSmallStyle, marginTop: '0.5rem' }}
                >
                  Clear selection (use all)
                </button>
              )}
            </>
          )}
        </div>
      </div>

      <div style={{ marginTop: '1rem' }}>
        <label style={{ fontSize: '0.9rem' }}>
          <input
            type="checkbox"
            checked={includeSummary}
            onChange={(e) => setIncludeSummary(e.target.checked)}
          />{' '}
          Include per-therapist summary sheet (applies pay splits)
        </label>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1.5rem' }}>
        <button onClick={onClose} style={btnStyle} disabled={downloading}>Cancel</button>
        <button onClick={handleDownload} style={btnPrimaryStyle} disabled={downloading}>
          {downloading ? 'Preparing…' : 'Download Excel'}
        </button>
      </div>
    </Modal>
  );
}

/* ============================================================
   Styles
============================================================ */
const containerStyle = {
  padding: '1.5rem',
  maxWidth: '1400px',
  margin: '0 auto',
  color: '#1f2937',
};
const pageHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '1rem',
};
const cardStyle = {
  background: '#fff',
  border: '1px solid var(--border-color)',
  borderRadius: '10px',
  padding: '1.25rem',
  marginBottom: '1.25rem',
  boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
};
const sectionHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '0.75rem',
  flexWrap: 'wrap',
  gap: '0.5rem',
};
const tableWrapStyle = {
  overflowX: 'auto',
  border: '1px solid var(--border-color)',
  borderRadius: '8px',
};
const tableStyle = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '0.9rem',
};
const thStyle = {
  background: '#f9fafb',
  textAlign: 'left',
  padding: '10px 12px',
  borderBottom: '1px solid var(--border-color)',
  position: 'sticky',
  top: 0,
  fontWeight: 600,
  color: 'var(--text-strong)',
  whiteSpace: 'nowrap',
};
const thSortStyle = { ...thStyle, cursor: 'pointer', userSelect: 'none' };
const tdStyle = {
  padding: '8px 12px',
  borderBottom: '1px solid rgba(255,255,255,0.4)',
  verticalAlign: 'middle',
};
const trStyle = { transition: 'background 0.15s' };
const inputStyle = {
  padding: '6px 8px',
  border: '1px solid var(--border-color)',
  borderRadius: '6px',
  fontSize: '0.9rem',
};
const selectStyle = { ...inputStyle, background: '#fff' };
const btnStyle = {
  padding: '8px 14px',
  border: '1px solid var(--border-color)',
  borderRadius: '6px',
  background: '#fff',
  cursor: 'pointer',
  fontSize: '0.9rem',
  fontWeight: 500,
  color: '#1f2937',
};
const btnPrimaryStyle = {
  ...btnStyle,
  background: 'var(--accent-color)',
  color: '#fff',
  borderColor: '#2563eb',
};
const btnDangerStyle = {
  background: 'var(--error-color)',
  color: '#fff',
  borderColor: 'var(--error-color)',
};
const btnSmallStyle = {
  ...btnStyle,
  padding: '4px 10px',
  fontSize: '0.82rem',
};
const filtersRowStyle = {
  display: 'flex',
  gap: '0.75rem',
  flexWrap: 'wrap',
  alignItems: 'flex-end',
};
const filterGroupStyle = { display: 'flex', flexDirection: 'column', gap: '3px' };
const filterLabelStyle = {
  fontSize: '0.8rem',
  color: '#4b5563',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.03em',
};
const pillStyle = {
  padding: '6px 14px',
  borderRadius: '999px',
  border: '1px solid var(--border-color)',
  background: '#fff',
  cursor: 'pointer',
  fontSize: '0.85rem',
  fontWeight: 500,
  color: '#4b5563',
};
const pillActiveStyle = {
  background: 'var(--accent-color)',
  color: '#fff',
  borderColor: '#2563eb',
};
const summaryStripStyle = {
  display: 'flex',
  gap: '2rem',
  padding: '0.85rem 1rem',
  background: '#f0f9ff',
  border: '1px solid #bae6fd',
  borderRadius: '8px',
  margin: '0.5rem 0',
};
const summaryLabelStyle = {
  fontSize: '0.75rem',
  color: '#075985',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
};
const summaryValueStyle = {
  fontSize: '1.4rem',
  fontWeight: 700,
  color: '#0c4a6e',
  marginTop: '2px',
};
const modalBackdropStyle = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(17, 24, 39, 0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
  padding: '1rem',
};
const modalCardStyle = {
  background: '#fff',
  borderRadius: '12px',
  width: '100%',
  maxWidth: '640px',
  maxHeight: '90vh',
  overflow: 'auto',
  boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
};
const modalHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '1rem 1.25rem',
  borderBottom: '1px solid var(--border-color)',
};
const modalCloseBtnStyle = {
  background: 'transparent',
  border: 'none',
  fontSize: '1.5rem',
  cursor: 'pointer',
  color: 'var(--text-muted)',
  lineHeight: 1,
};
const modalTableStyle = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '0.9rem',
};
const modalScrollBoxStyle = {
  border: '1px solid var(--border-color)',
  borderRadius: '6px',
  padding: '0.5rem 0.75rem',
  maxHeight: '180px',
  overflowY: 'auto',
  marginTop: '3px',
};

export default AdminDailyReportPage;
